import { AvatarNext as Avatar } from '@momentum-ui/react-collaboration';
import type { PresenceType } from '@momentum-ui/react-collaboration';
import React, { useCallback } from 'react';

import { useLazyLoadAvatarUrl } from '../../hooks/useLazyLoadAvatarUrl';
import { requestParticipantAvatarUrl } from '../../services/avatarService';
import { removeBracketsAndContent } from '../../utils/avatarInitials';

type CallHistoryAvatarSize = React.ComponentProps<typeof Avatar>['size'];

export interface ICallHistoryAvatarProps {
  remotePartyId?: string;
  fallbackId: string;
  displayName: string;
  title?: string;
  presenceType?: PresenceType;
  presenceLabel?: string;
  size: CallHistoryAvatarSize;
  requestSize: 40 | 80;
}

const INVALID_REMOTE_PARTY_ID = '00000000-0000-0000-0000-000000000000';

export const CallHistoryAvatar = ({
  remotePartyId,
  fallbackId,
  displayName,
  title,
  presenceType,
  presenceLabel,
  size,
  requestSize,
}: ICallHistoryAvatarProps): JSX.Element => {
  const validRemotePartyId = remotePartyId?.trim();
  const shouldRequestAvatar =
    Boolean(validRemotePartyId) &&
    validRemotePartyId !== INVALID_REMOTE_PARTY_ID;
  const fetchAvatarUrl = useCallback(
    () =>
      requestParticipantAvatarUrl(validRemotePartyId!, { size: requestSize }),
    [requestSize, validRemotePartyId]
  );
  const [avatarUrl] = useLazyLoadAvatarUrl(
    validRemotePartyId || fallbackId,
    shouldRequestAvatar ? fetchAvatarUrl : undefined
  );

  return (
    <Avatar
      initials={removeBracketsAndContent(displayName)}
      title={title || displayName}
      size={size}
      src={avatarUrl}
      {...(presenceType ? { presence: presenceType } : {})}
      {...(presenceLabel ? { presenceLabel } : {})}
    />
  );
};
